import {
  type AppBskyActorDefs,
  type AppBskyUnspeccedGetSuggestedOnboardingUsers,
} from '@atproto/api'
import {type QueryClient, useQuery} from '@tanstack/react-query'

import {logger} from '#/logger'
import {STALE} from '#/state/queries'
import {useAgent, useSession} from '#/state/session'
import {type AccountType} from '#/screens/Onboarding/state'

export type QueryProps = {
  accountType: AccountType | null
  country?: string | null
  learningStage?: string | null
  topics?: string[]
  familyStages?: string[]
  parentTopics?: string[]
  childTopics?: string[]
  creatorRole?: string | null
  audience?: string[]
  orgType?: string | null
  limit?: number
  enabled?: boolean
}

export const getSuggestedOnboardingUsersQueryKeyRoot =
  'unspecced-suggested-onboarding-users'
export const createGetSuggestedOnboardingUsersQueryKey = (
  props: QueryProps,
) => [
  getSuggestedOnboardingUsersQueryKeyRoot,
  props.accountType,
  props.country,
  props.learningStage,
  props.topics?.join(','),
  props.familyStages?.join(','),
  props.parentTopics?.join(','),
  props.childTopics?.join(','),
  props.creatorRole,
  props.audience?.join(','),
  props.orgType,
  props.limit,
]

export function useGetSuggestedOnboardingUsersQuery(props: QueryProps) {
  const agent = useAgent()
  const {hasSession} = useSession()

  return useQuery({
    enabled: hasSession && !!props.accountType && props.enabled !== false,
    staleTime: STALE.MINUTES.THREE,
    queryKey: createGetSuggestedOnboardingUsersQueryKey(props),
    queryFn: async () => {
      const body: Record<string, unknown> = {
        accountType: props.accountType,
        limit: props.limit || 25,
      }

      if (props.country) body.country = props.country
      if (props.learningStage) body.learningStage = props.learningStage
      if (props.familyStages?.length) body.familyStages = props.familyStages
      if (props.parentTopics?.length) body.parentTopics = props.parentTopics
      if (props.childTopics?.length) body.childTopics = props.childTopics
      if (props.creatorRole) body.creatorRole = props.creatorRole
      if (props.orgType) body.orgType = props.orgType

      if (props.accountType === 'organisation') {
        if (props.audience?.length) body.orgAudience = props.audience
        if (props.topics?.length) body.orgTopics = props.topics
      } else {
        if (props.audience?.length) body.audience = props.audience
        if (props.topics?.length) body.topics = props.topics
      }

      const {data} = (await agent.call(
        'app.bsky.unspecced.getSuggestedOnboardingUsers',
        undefined,
        body,
      )) as {data: AppBskyUnspeccedGetSuggestedOnboardingUsers.OutputSchema}

      if (!data.recIdStr) {
        logger.debug('getSuggestedOnboardingUsers response missing recIdStr')
      }
      return {...data, recId: data.recIdStr}
    },
  })
}

export function* findAllProfilesInQueryData(
  queryClient: QueryClient,
  did: string,
): Generator<AppBskyActorDefs.ProfileView, void> {
  const responses =
    queryClient.getQueriesData<AppBskyUnspeccedGetSuggestedOnboardingUsers.OutputSchema>(
      {
        queryKey: [getSuggestedOnboardingUsersQueryKeyRoot],
      },
    )
  for (const [_key, response] of responses) {
    if (!response) {
      continue
    }

    for (const actor of response.actors) {
      if (actor.did === did) {
        yield actor
      }
    }
  }
}
